/** @jsxImportSource @emotion/react */
import React, { useState, useImperativeHandle, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import _ from 'lodash';
import { DataType, addClass, hasClass } from '@grapecity/wijmo';
import { CellType, DataMap, CellRangeEventArgs } from '@grapecity/wijmo.grid';
import { Button } from '@mui/material';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { IconButton } from 'components/buttons/IconSVG';
import { MdDetailInspection, MdArticleCondition, MdArticle } from 'models/md/MdArticle';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { findMdDetailInspectionList, saveMdDetailInspectionList } from 'apis/md/MdArticle';
import { useLoading } from 'components/process/Loading';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { CrudCode } from 'models/common/Edit';
import { CommonUtil } from 'utils/CommonUtil';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { findMdIvesAtcRegList } from 'apis/md/MdIvesAtcRegt';
import { Code, commonYNList } from 'models/common/CommonCode';

interface Props {
  isReadOnly?: boolean;
}

/**
 * @desc 투자비관리 > 기준정보 > 투자비 산출 항목 관리 > [상세 점검사항] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.27   jshwang12       신규 생성
 */
const DtlIsptMatr = ({ isReadOnly = true, ...props }: Props, ref) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { isLoading, openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const [condition, setCondition] = useState<MdArticleCondition>();
  const [rowData, setRowData] = useState<MdDetailInspection[]>([]);
  const [code, setCode] = useState<any>();
  const dragIndex = useRef<any>();

  useImperativeHandle(ref, () => ({
    search: async (params) => {
      setCondition(params);
      handleSearch(params);
    },
  }));

  useEffect(() => {
    getCommonCodes();
  }, []);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNamesCondition({
        cmnGrCd: 'PRDN_PROC_CD',
        optValCtn7: 'Y', // 형개발투자비 산출
      }),
      getCommonCodeNamesCondition({
        cmnGrCd: 'MD_EQCL_ID',
        optValCtn2: 'Y', // 투자비항목대상
      }),
      getCommonCodeNamesCondition({
        cmnGrCd: 'MD_EQCL_ID',
        optValCtn1: 'Y', // 설비사양대상 (설비기준)
      }),
      getCommonCodeNames('MD_CELL_DIVS_CD'),
      getCommonCodeNames('MD_QTY_UNIT_CD'),
      getCommonCodeNames('MD_UPRC_DCN_TP_CD'),
      findMdIvesAtcRegList({ stndYn: 'N' }), // 비표준 투자항목 조회
    ]).then(
      ([prdnProcCd, mdEqclId, prdnEqpId, mdCellDivsCd, mdQtyUnitCd, mdUprcDcnTpCd, mdAtcList]) => {
        setCode({
          prdnProcCd: prdnProcCd,
          mdEqclId: mdEqclId,
          prdnEqpId: prdnEqpId,
          mdCellDivsCd: mdCellDivsCd,
          mdQtyUnitCd: mdQtyUnitCd,
          mdUprcDcnTpCd: mdUprcDcnTpCd,
          mdAtcList: (mdAtcList || []).reduce(
            (acc, cur) => [
              ...acc,
              {
                cmnCd: cur.ivesAtcId,
                cmnCdNm: cur.ivesAtcNm,
              },
            ],
            [] as Code[]
          ),
        });
      }
    );
  };

  const handleSearch = (params) => {
    findMdDetailInspectionList(params).then((result) => {
      setRowData(result);
    });
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('md.label.상세점검사항 목록', '상세점검사항 목록'),
      t('md.label.상세점검사항 목록', '상세점검사항 목록')
    );
  };

  const handleAddRow = () => {
    const newData: MdArticle = {
      crudKey: CrudCode.CREATE,
    };
    setRowData((prev) => [newData, ...prev]);
  };

  const handleDelRow = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);

    if ((selectedRows || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.삭제할 데이터를 선택해 주세요.', '삭제할 데이터를 선택해 주세요.'),
      });
      return;
    }

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        // 각 선택된 아이템에 대해 삭제 로직 수행
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
    gridRef.current?.collectionView.refresh();
  };

  const validate = () => {
    // 수정사항 없음
    if ((rowData || []).filter((o) => !!o.crudKey).length < 1) {
      return false;
    }

    const valid = [] as string[];
    const pkMap = new Map();

    (rowData || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .forEach((o, index) => {
        if (_.isEmpty(o.prdnProcCd)) {
          valid.push(
            `${index + 1} : ${t('md.label.공정을 선택해 주세요.', '공정을 선택해 주세요.')}`
          );
          return;
        }
        if (_.isEmpty(o.eqclId)) {
          valid.push(
            `${index + 1} : ${t('md.label.설비군을 선택해 주세요.', '설비군을 선택해 주세요.')}`
          );
          return;
        }
        if (_.isEmpty(o.dtlIsptId)) {
          valid.push(
            `${index + 1} : ${t(
              'md.label.비표준항목을 선택해 주세요.',
              '비표준항목을 선택해 주세요.'
            )}`
          );
          return;
        }
        const pkKey = `${o.prdnProcCd}|${o.eqclId}|${o.dtlIsptId}`;
        if (pkMap.has(pkKey)) {
          valid.push(
            `${index + 1} : ${t(
              'md.label.중복된 데이터가 존재합니다.',
              '중복된 데이터가 존재합니다.'
            )}`
          );
          return;
        } else {
          pkMap.set(pkKey, index);
        }
        if (_.isEmpty(o.uprcDcnTpCd)) {
          valid.push(
            `${index + 1} : ${t('md.label.Ref 기준을 선택해 주세요.', 'Ref 기준을 선택해 주세요.')}`
          );
          return;
        }
      });

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.join('<br/>') });
      return false;
    }

    return true;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }
    // 추가/변경 중 QTY_UNIT_CD가 EXCP의 경우는 저장전에 확인 메시지를 표시
    const confirmMsg = (rowData || [])
      .filter((o) => o.crudKey && [CrudCode.CREATE, CrudCode.UPDATE].includes(o.crudKey))
      .find((o) => o.qtyUnitCd === 'EXCP')
      ? t(
          'md.label.수량 기준이 EXCP(예외)의 경우는 PI에 연락하여 수량 계산 로직을 반영하시기 바랍니다.<br/>추가 로직이 반영되지 않으면 수량이 반영되지 않고 투자비산출시 입력 하셔야 합니다.',
          '수량 기준이 EXCP(예외)의 경우는 PI에 연락하여 수량 계산 로직을 반영하시기 바랍니다.<br/>추가 로직이 반영되지 않으면 수량이 반영되지 않고 투자비산출시 입력 하셔야 합니다.'
        )
      : t(`com.msg.변경사항을 저장하시겠습니까?`, `변경사항을 저장하시겠습니까?`);

    openCommonModal({
      modalType: 'confirm',
      content: confirmMsg,
      yesCallback: () => {
        openLoading(true);
        saveMdDetailInspectionList(rowData)
          .then((res) => {
            openMessageBar({
              type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
              content:
                res?.successOrNot === 'Y'
                  ? t('com.label.저장되었습니다.', '저장되었습니다.')
                  : res?.data && typeof res?.data === 'string'
                  ? res.data
                  : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
            if (res?.successOrNot === 'Y') {
              handleSearch(condition);
            }
          })
          .finally(() => openLoading(false));
      },
    });
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'sortable',
        header: String(t('com.label.순서', '순서')),
        minWidth: 43,
        width: 43,
        visible: !isReadOnly,
        align: 'center',
        isReadOnly: true,
        cssClass: 'WijmoCellMove',
        cellTemplate: '<Button />',
      },
      {
        binding: 'prdnProcCd',
        header: String(t('md.label.공정', '공정')),
        align: 'center',
        minWidth: 40,
        width: 100,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.prdnProcCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'eqclId',
        header: String(t('md.label.설비군', '설비군')),
        align: 'center',
        minWidth: 40,
        width: 90,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.mdEqclId || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'dtlIsptId',
        header: String(t('md.label.비표준항목', '비표준항목')),
        align: 'center',
        minWidth: 40,
        width: 162,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.mdAtcList || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        header: String(t('md.label.수량기준', '수량기준')),
        columns: [
          {
            binding: 'qtyUnitCd',
            header: String(t('md.label.기준', '기준')),
            align: 'center',
            minWidth: 50,
            width: 150,
            isRequired: false,
            cssClass: 'WijmoSelect',
            dataMap: new DataMap(code?.mdQtyUnitCd || [], 'cmnCd', 'cmnCdNm'),
          },
          {
            binding: 'msrScf',
            header: String(t('md.label.배수', '배수')),
            align: 'center',
            minWidth: 40,
            width: 64,
            dataType: DataType.String,
            isRequired: false,
          },
          {
            binding: 'addQty',
            header: String(t('md.label.추가', '추가')),
            align: 'center',
            minWidth: 40,
            width: 64,
            dataType: DataType.String,
            isRequired: false,
          },
          {
            binding: 'calLogcCtn',
            header: String(t('md.label.계산로직', '계산로직')),
            align: 'left',
            minWidth: 70,
            width: '*',
            isRequired: false,
          },
        ],
      },
      {
        binding: 'prdnEqpId',
        header: String(t('md.label.설비기준', '설비기준')),
        align: 'center',
        minWidth: 40,
        width: 80,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.prdnEqpId || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'uprcDcnTpCd',
        header: String(t('md.label.Ref 기준', 'Ref 기준')),
        align: 'center',
        minWidth: 60,
        width: 118,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.mdUprcDcnTpCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'uprcDcnCtn',
        header: String(t('md.label.단가기준', '단가기준')),
        align: 'left',
        minWidth: 70,
        width: 100,
        isRequired: false,
      },
      {
        binding: 'useYn',
        header: String(t('com.label.사용여부', '사용여부')),
        align: 'center',
        minWidth: 70,
        width: 100,
        dataMap: new DataMap(commonYNList, 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        align: 'center',
        minWidth: 40,
        width: 120,
        isReadOnly: true,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        align: 'center',
        minWidth: 40,
        width: 120,
        isReadOnly: true,
      },
    ];
  }, [code]);

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      if (CrudCode.CREATE !== item.crudKey) {
        // 수정 시 키값 수정불가
        if (['prdnProcCd', 'eqclId', 'dtlIsptId'].includes(binding)) {
          cell.ariaReadOnly = true;
          if (cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
      // 수량 > 배수 || 수량 > 추가
      if ('msrScf' === binding || 'addQty' === binding) {
        // 수량_기준이 EXCP인 경우 입력불가
        if ('EXCP' === item.qtyUnitCd) {
          cell.ariaReadOnly = true;
        }
      }
      // 설비기준
      else if ('prdnEqpId' === binding) {
        // 수량_기준이 PRJ, EXCP인 경우 입력불가
        if (['PRJ', 'EXCP'].includes(item.qtyUnitCd || '')) {
          cell.ariaReadOnly = true;
          if (cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
      // 사용여부
      else if ('useYn' === binding) {
        // 진행중인 프로젝트에서 사용중이면 사용여부 변경 불가
        if ('Y' === item.ivesMdlIngExistYn) {
          cell.ariaReadOnly = true;
          if (cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
    }
  });

  const onBeginningEdit = useEvent((grid, e) => {
    if (isReadOnly) {
      e.cancel = true;
    } else {
      const col = grid.columns[e.col];
      const binding = col.binding;
      const item = grid.rows[e.row].dataItem;

      if (CrudCode.CREATE !== item.crudKey) {
        // 수정 시 키값 수정불가
        if (['prdnProcCd', 'eqclId', 'dtlIsptId'].includes(binding)) {
          e.cancel = true;
        }
      }
      // 수량 > 배수 || 수량 > 추가
      if ('msrScf' === binding || 'addQty' === binding) {
        // 수량_기준이 EXCP인 경우 입력불가
        if ('EXCP' === item.qtyUnitCd) {
          e.cancel = true;
        }
      }
      // 설비기준
      else if ('prdnEqpId' === binding) {
        // 수량_기준이 PRJ, EXCP인 경우 입력불가
        if (['PRJ', 'EXCP'].includes(item.qtyUnitCd || '')) {
          e.cancel = true;
        }
      }
      // 사용여부
      else if ('useYn' === binding) {
        // 진행중인 프로젝트에서 사용중이면 사용여부 변경 불가
        if ('Y' === item.ivesMdlIngExistYn) {
          e.cancel = true;
        }
      }
    }
  });

  /**
   * 설비기준 기본값
   * @param eqclId
   * @param qtyUnitCd
   */
  const getDefaultPrdnEqpId = (eqclId, qtyUnitCd) => {
    // 설비기준 기본값 설정 시 수량_기준이 PRJ, EXCP인 경우 제외
    if (eqclId && qtyUnitCd && !['PRJ', 'EXCP'].includes(qtyUnitCd)) {
      // 선택한 투자항목 설비군과 동일한 설비사양 설비군이 있으면 기본값으로 적용
      return (code?.prdnEqpId || []).find((o) => o.cmnCd === eqclId)?.cmnCd || '';
    }
    return '';
  };

  /**
   * 계산로직 기본값
   * @param qtyUnitCd
   * @param msrScf
   * @param addQty
   */
  const getDefaultCalLogcCtn = (qtyUnitCd, msrScf, addQty) => {
    if (qtyUnitCd) {
      // 수량_기준이 EXCP인 경우는 사용자 별도 입력
      if ('EXCP' !== qtyUnitCd) {
        const qtyUnitCdNm =
          (code?.mdQtyUnitCd || []).find((o) => o.cmnCd === qtyUnitCd)?.cmnCdNm || '';
        const arr = [qtyUnitCdNm, msrScf && `* ${msrScf}`, addQty && `+ ${addQty}`].filter(
          (o) => !!o
        );

        return arr.join(' ');
      }
    }
    return '';
  };

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;
    const oldVal = grid.getCellData(e.row, e.col);
    const newVal = grid.activeEditor?.value;

    // 설비군
    if ('eqclId' === binding) {
      const newEqclId = (code?.mdEqclId || []).find((o) => o.cmnCdNm === newVal)?.cmnCd || '';
      item.prdnEqpId = getDefaultPrdnEqpId(newEqclId, item.qtyUnitCd); // 설비기준 기본값 설정
    }
    // 수량 > 기준
    else if ('qtyUnitCd' === binding) {
      const newQtyUnitCd = (code?.mdQtyUnitCd || []).find((o) => o.cmnCdNm === newVal)?.cmnCd || '';
      item.prdnEqpId = getDefaultPrdnEqpId(item.eqclId, newQtyUnitCd); // 설비기준 기본값 설정
      if ('EXCP' === newQtyUnitCd) {
        // 수량 > 배수, 추가 초기화
        item.msrScf = '';
        item.addQty = '';
      }
      item.calLogcCtn = getDefaultCalLogcCtn(newQtyUnitCd, item.msrScf, item.addQty); // 계산로직 기본값 설정
    }
    // 수량 > 배수
    else if ('msrScf' === binding) {
      if (newVal) {
        if (!/^-?[0-9.]+$/.test(newVal)) {
          e.cancel = true;
          return;
        }
        const value = parseFloat(newVal);
        if (!_.isNaN(value)) {
          const newMsrScf = CommonUtil.roundNumber(value, 2);
          setTimeout(() => {
            grid.setCellData(e.row, e.col, newMsrScf);
          }, 0);
          item.calLogcCtn = getDefaultCalLogcCtn(item.qtyUnitCd, newMsrScf, item.addQty); // 계산로직 기본값 설정
        }
      }
    }
    // 수량 > 추가
    else if ('addQty' === binding) {
      // 숫자만 입력 가능
      if (newVal) {
        if (!/^[0-9]+$/.test(newVal)) {
          e.cancel = true;
          return;
        } else {
          item.calLogcCtn = getDefaultCalLogcCtn(item.qtyUnitCd, item.msrScf, newVal); // 계산로직 기본값 설정
        }
      }
    }
  });

  const onCellEditEnded = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.formatItem.addHandler((s, e) => {
      const item = s.rows[e.row]?.dataItem;
      if (e.panel.cellType === CellType.TopLeft && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }
      if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          if (item?.ivesMdlExistYn === 'Y') {
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }
      const binding = s.columns[e.col].binding;
      if (s.cells === e.panel) {
        if ('sortable' === binding) {
          e.cell.draggable = true;
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      // 전체 선택 시
      if (hasClass(e.target, 'wj-column-selector-group') && e.target.checked) {
        grid.deferUpdate(() => {
          grid.rows.forEach((row) => {
            if (row?.dataItem?.ivesMdlExistYn === 'Y') {
              row.isSelected = false;
            }
          });
        });
      }
    });

    grid.hostElement.addEventListener('dragstart', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggingRow(new CellRangeEventArgs(hti.panel, hti.range));
    });
    grid.hostElement.addEventListener('dragover', (e) => {
      const hti = grid.hitTest(e);
      if (hti.panel === grid.cells) {
        e.preventDefault();
      }
    });
    grid.hostElement.addEventListener('drop', (e) => {
      const hti = grid.hitTest(e);
      grid.onDraggedRow(new CellRangeEventArgs(hti.panel, hti.range));
    });
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.상세 점검사항', '상세 점검사항')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {!isReadOnly && (
            <>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.button.행추가', '행추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={449}
        isReadOnly={isReadOnly}
        allowPinning={false}
        allowSorting={false}
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
        cellEditEnding={onCellEditEnding}
        cellEditEnded={onCellEditEnded}
        excludeFilter={['sortable']}
        draggingRow={(grid, e) => {
          dragIndex.current = e.row;
          grid.collectionView.moveCurrentToPosition(e.row);
        }}
        draggedRow={(grid, e) => {
          const dropIndex = e.row;
          const arr = grid.collectionView.sourceCollection;
          grid.collectionView.deferUpdate(() => {
            const item = arr[dragIndex.current];
            arr.splice(dragIndex.current, 1);
            arr.splice(dropIndex, 0, item);
            grid.collectionView.moveCurrentToPosition(dropIndex);
            if (item.crudKey !== CrudCode.CREATE) {
              item.crudKey = CrudCode.UPDATE;
            }
          });
        }}
      />
      {!isReadOnly && (
        <GlobalBtnGroup>
          <Button
            css={IconButton.button}
            className="save"
            onClick={() => handleSave()}
            disabled={isLoading}
          >
            {t('com.button.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}
    </>
  );
};

export default React.forwardRef(DtlIsptMatr);
